import React from "react";
import { useLocation } from "react-router-dom";
import { Dot } from "lucide-react";
import Reveal from "@/components/Reveal";
import { PRIVACY } from "@/data/privacy";
import { TERMS } from "@/data/terms";

function Items({ items }) {
  return (
    <ul className="mt-4 space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5 text-[#4B2E5A]/70">
          <Dot className="mt-1 h-5 w-5 shrink-0 text-[#F28C82]" />
          <span className="leading-relaxed">{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Section({ section }) {
  return (
    <div className="mb-12">
      <h2 className="font-heading text-2xl font-medium text-[#4B2E5A]">{section.title}</h2>
      {section.intro && <p className="mt-4 leading-relaxed text-[#4B2E5A]/70">{section.intro}</p>}
      {section.body &&
        section.body.map((p, i) => (
          <p key={i} className="mt-4 leading-relaxed text-[#4B2E5A]/70">{p}</p>
        ))}
      {section.items && <Items items={section.items} />}
      {section.note && (
        <p className="mt-4 rounded-2xl border border-[#F5C86B]/40 bg-[#F5C86B]/10 px-5 py-4 text-sm leading-relaxed text-[#4B2E5A]/80">
          {section.note}
        </p>
      )}
      {section.subsections &&
        section.subsections.map((sub, i) => (
          <div key={i} className="mt-6">
            <h3 className="font-heading text-lg font-medium text-[#4B2E5A]">{sub.heading}</h3>
            {sub.intro && <p className="mt-2 leading-relaxed text-[#4B2E5A]/70">{sub.intro}</p>}
            {sub.items && <Items items={sub.items} />}
            {sub.note && (
              <p className="mt-3 rounded-2xl border border-[#F5C86B]/40 bg-[#F5C86B]/10 px-5 py-4 text-sm leading-relaxed text-[#4B2E5A]/80">
                {sub.note}
              </p>
            )}
          </div>
        ))}
    </div>
  );
}

export default function Legal() {
  const { pathname } = useLocation();
  const isPrivacy = pathname.includes("privacy");
  const doc = isPrivacy ? PRIVACY : TERMS;
  const title = isPrivacy ? "Privacy Policy" : "Terms & Conditions";

  return (
    <div data-testid="legal-page" className="pt-32">
      <section className="relative overflow-hidden py-12 md:py-16">
        <div className="mesh-bg">
          <div className="mesh-blob" style={{ width: 400, height: 400, background: "#B8E3D9", top: -100, right: -60 }} />
        </div>
        <div className="relative z-10 mx-auto max-w-3xl px-6 md:px-12">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F28C82]">Legal</p>
            <h1 className="mt-4 font-heading text-4xl font-light tracking-tighter text-[#4B2E5A] md:text-5xl">{title}</h1>
            <p className="mt-4 text-sm text-[#4B2E5A]/50">{doc.effective} &nbsp;|&nbsp; {doc.updated}</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-16">
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <Reveal>
            <p className="mb-12 leading-relaxed text-[#4B2E5A]/70">{doc.intro}</p>
          </Reveal>
          {doc.sections.map((section, i) => (
            <Reveal key={i} delay={0.03}>
              <Section section={section} />
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
